<template>
    <div class="body">
        <div class="container">
            <div class="header">
                <h1>AI图像生成</h1>
                <p>用文字描述你的想象，让AI为你创造精美的图像</p>
            </div>

            <div class="main-content">
                <!-- 左侧图片展示区 -->
                <div class="left-panel">
                    <div class="panel-title">
                        <i class="el-icon-picture"></i>
                        生成历史
                        <div class="stats-bar" style="margin-left: auto; margin-bottom: 0;">
                            <span>已生成：{{ generatedImages.length }} 张</span>
                            <!-- <el-button size="mini" @click="clearHistory" type="text">清空历史</el-button> -->
                        </div>
                    </div>
                    
                    <div v-if="generatedImages.length > 0" class="image-grid" v-infinite-scroll="getList">
                        <div 
                            v-for="(image, index) in generatedImages" 
                            :key="index" 
                            class="image-item"
                            @click="viewImage(image)"
                        >
                            <img :src="image.image" :alt="image.prompt">
                            <div class="image-overlay">
                                <!-- <div style="font-weight: bold;">{{ image.resolution }}</div> -->
                                <div style="opacity: 0.9; margin-top: 2px;">{{ truncateText(image.prompt, 30) }}</div>
                            </div>
                        </div>
                    </div>
                    
                    <div v-else class="empty-state">
                        <i class="el-icon-camera"></i>
                        <h3>还没有生成过图片</h3>
                        <p>在右侧输入提示词，开始创作你的第一张AI图片吧！</p>
                    </div>
                </div>

                <!-- 右侧配置区 -->
                <div class="right-panel">
                    <div class="panel-title">
                        <i class="el-icon-setting"></i>
                        生成配置
                    </div>

                    <!-- 提示词 -->
                    <div class="form-section">
                        <label>提示词</label>
                        <el-input
                            v-model="form.prompt"
                            type="textarea"
                            :rows="4"
                            placeholder="描述你想要生成的图像，例如：一只可爱的小猫在花园里玩耍，阳光明媚，高清，逼真准确"
                        ></el-input>
                    </div>

                    <!-- 反向提示词 -->
                    <div class="form-section">
                        <label>反向提示词</label>
                        <el-input
                            v-model="form.negativePrompt"
                            type="textarea"
                            :rows="3"
                            placeholder="描述你不希望出现的元素，例如：低分辨率、错误、最差质量、低质量、残缺、多余的手指、比例不良等"
                        ></el-input>
                    </div>

                    <!-- 图片分辨率 -->
                    <div class="form-section">
                        <label>图片分辨率</label>
                        <div class="resolution-options">
                            <div 
                                v-for="resolution in resolutions" 
                                :key="resolution.value"
                                class="resolution-card"
                                :class="{ active: form.resolution === resolution.value }"
                                @click="form.resolution = resolution.value"
                            >
                                <div style="font-weight: bold;">{{ resolution.ratio }}</div>
                                <div style="font-size: 0.8rem; opacity: 0.7;">{{ resolution.value }}</div>
                            </div>
                        </div>
                    </div>

                    <!-- 水印开关 -->
                    <!-- <div class="form-section">
                        <el-row type="flex" justify="space-between" align="middle">
                            <el-col>
                                <label>🏷️ 添加水印</label>
                            </el-col>
                            <el-col>
                                <el-switch v-model="form.watermark"></el-switch>
                            </el-col>
                        </el-row>
                    </div> -->

                    <!-- 生成按钮 -->
                    <button 
                        class="generate-btn" 
                        @click="generateImage"
                        :disabled="!form.prompt.trim() || isGenerating"
                    >
                        <span v-if="isGenerating">
                            <i class="el-icon-loading"></i> 生成中...
                        </span>
                        <span v-else>
                            生成图像
                        </span>
                    </button>

                    <!-- 示例区域 -->
                    <div class="examples-section">
                        <h4 style="margin-bottom: 15px; color: #2c3e50;">💡 创作示例</h4>
                        <div v-for="example in examples" :key="example.title" class="example-item">
                            <img 
                                :src="example.thumbnail" 
                                :alt="example.title"
                                class="example-thumbnail"
                                @click="viewExampleImage(example)"
                            >
                            <div class="example-content">
                                <div class="example-title">{{ example.title }}</div>
                                <div class="example-prompt">{{ example.prompt }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 加载遮罩 -->
        <div v-if="isGenerating" class="loading-overlay">
            <div class="loading-content">
                <el-progress :percentage="progress" :show-text="false"></el-progress>
                <h3 style="margin: 20px 0 10px 0;">AI正在创作中...</h3>
                <p style="color: #666;">{{ loadingMessages[currentLoadingMessage] }}</p>
            </div>
        </div>

        <!-- 图片预览对话框 -->
        <el-dialog
            :visible.sync="previewVisible"
            title="图片详情"
            width="80%"
            top="10vh"
        >
            <div v-if="selectedImage" style="text-align: center;">
                <img :src="selectedImage.image" style="max-width: 100%; max-height: 60vh; border-radius: 8px;">
                <div style="margin-top: 20px; text-align: left;">
                    <p><strong>提示词：</strong>{{ selectedImage.prompt }}</p>
                    <p v-if="selectedImage.negativePrompt"><strong>反向提示词：</strong>{{ selectedImage.negativePrompt }}</p>
                    <p><strong>分辨率：</strong>{{ selectedImage.resolution }}</p>
                    <p><strong>生成时间：</strong>{{ selectedImage.createtime_text }}</p>
                </div>
            </div>
        </el-dialog>
    </div>
</template>
<script>
import { baseURL } from '@/utils/request'
export default {
    data() {
        return {
            page:1,
            loading: false,
            finished: false,
            form: {
                prompt: '',
                negativePrompt: '',
                resolution: '1328*1328',
                watermark: false
            },
            resolutions: [
                { value: '1328*1328', ratio: '1:1' },
                { value: '1140*1472', ratio: '3:4' },
                { value: '1472*1140', ratio: '4:3' },
                { value: '928*1664', ratio: '9:16' },
                { value: '1664*928', ratio: '16:9' },
            ],
            examples: [
                {
                    title: '风景摄影',
                    prompt: '壮观的日出景色，金色的阳光洒在雪山上，湖水倒映着天空，高清摄影，自然光线，广角镜头',
                    thumbnail: baseURL+'/uploads/give/image/file_690d873f479d3.png'
                },
                {
                    title: '艺术插画',
                    prompt: '可爱的动漫女孩，粉色长发，大眼睛，穿着白色连衣裙，樱花背景，水彩画风格，柔和色调',
                    thumbnail: baseURL+'/uploads/give/image/file_690d87fac3fd0.png'
                },
                {
                    title: '科幻未来',
                    prompt: '赛博朋克城市夜景，霓虹灯光，高楼大厦，飞行汽车，科幻感，电影级画质，蓝紫色调',
                    thumbnail: baseURL+'/uploads/give/image/file_690d887a8f2fd.png'
                }
            ],
            generatedImages: [],
            isGenerating: false,
            progress: 0,
            currentLoadingMessage: 0,
            loadingMessages: [
                '分析提示词中...',
                '构建画面布局...',
                '添加色彩与细节...',
                '优化图像质量...',
                '即将完成...'
            ],
            previewVisible: false,
            selectedImage: null
        }
    },
    created(){
        this.getList()
    },
    methods: {
        async getList(){
            if (this.loading || this.finished) return
            this.loading = true
            try {
                const res = await this.$api.post('/api/production/imgGendrationList', {page:this.page})
                const list = res.data.list.data || []
                if (list.length > 0) {
                    this.generatedImages.push(...list)
                    this.page++
                } else {
                    this.finished = true
                }
            } catch (err) {
                console.error('加载图片失败：', err)
            } finally {
                this.loading = false
            }
        },
        useExample(example) {
            this.form.prompt = example.prompt;
            this.$message.success('已应用示例提示词！');
        },
        
        viewExampleImage(example) {
            this.selectedImage = {
                image: example.thumbnail.replace('200/200', '800/600'), // 使用更大尺寸
                prompt: example.prompt,
                negativePrompt: '',
                resolution: '示例图片',
                createtime_text: '示例'
            };
            this.previewVisible = true;
        },
        
        viewImage(image) {
            this.selectedImage = image;
            this.previewVisible = true;
        },
        
        async generateImage() {
            if (!this.form.prompt.trim()) {
                this.$message.warning('请输入提示词');
                return;
            }

            this.isGenerating = true;
            this.progress = 0;
            this.currentLoadingMessage = 0;

            // 模拟生成进度
            const progressInterval = setInterval(() => {
                this.progress += Math.random() * 15;
                if (this.progress > 90) {
                    this.progress = 90;
                }
                
                // 切换加载消息
                if (this.progress > 20 && this.currentLoadingMessage < 1) {
                    this.currentLoadingMessage = 1;
                } else if (this.progress > 40 && this.currentLoadingMessage < 2) {
                    this.currentLoadingMessage = 2;
                } else if (this.progress > 60 && this.currentLoadingMessage < 3) {
                    this.currentLoadingMessage = 3;
                } else if (this.progress > 80 && this.currentLoadingMessage < 4) {
                    this.currentLoadingMessage = 4;
                }
            }, 200);

            try {
                const res = await this.$api.post('/api/production/imgGeneration', this.form)
                if(res.code == 1){
                    const newImage = {
                        image: res.data.image,
                        prompt: this.form.prompt,
                        negativePrompt: this.form.negativePrompt,
                        resolution: this.form.resolution,
                        createtime_text: new Date().toLocaleString('zh-CN')
                    };
                    this.generatedImages.unshift(newImage);
                    this.$message.success(res.msg);
                }else{
                    this.$message.error(res.msg);
                }
            } catch (err) {
                console.error('获取失败:', err)
                this.$message.error(err.message || '数据获取失败')
            } finally {
                clearInterval(progressInterval);
                this.progress = 100;
                this.isGenerating = false;
            }
        },
        
        clearHistory() {
            this.$confirm('确定要清空所有生成历史吗？', '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                this.generatedImages = [];
                this.$message.success('历史记录已清空');
            });
        },
        
        truncateText(text, maxLength) {
            return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
        }
    }
}
</script>
<style scoped>
    * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
    }

    .body {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        min-height: 100vh;
        font-family: 'Helvetica Neue', Arial, sans-serif;
    }

    .container {
        /* max-width: 1400px; */
        margin: 0 auto;
        padding: 20px;
        min-height: 100vh;
    }

    .header {
        text-align: center;
        margin-bottom: 30px;
        color: white;
    }

    .header h1 {
        font-size: 2.5rem;
        margin-bottom: 10px;
        text-shadow: 0 2px 4px rgba(0,0,0,0.3);
    }

    .header p {
        font-size: 1.1rem;
        opacity: 0.9;
    }

    .main-content {
        display: flex;
        gap: 20px;
        min-height: 70vh;
    }

    .left-panel {
        flex: 2;
        background: rgba(255, 255, 255, 0.95);
        border-radius: 16px;
        padding: 20px;
        box-shadow: 0 8px 32px rgba(0,0,0,0.1);
        backdrop-filter: blur(10px);
        max-height: 80vh;
    }

    .right-panel {
        flex: 1;
        background: rgba(255, 255, 255, 0.95);
        border-radius: 16px;
        padding: 20px;
        box-shadow: 0 8px 32px rgba(0,0,0,0.1);
        backdrop-filter: blur(10px);
        max-height: 80vh;
        overflow-y: auto;
    }

    .panel-title {
        font-size: 1.3rem;
        font-weight: bold;
        margin-bottom: 20px;
        color: #2c3e50;
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .panel-title i {
        color: #667eea;
    }

    .image-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 15px;
        max-height: 68vh;
        overflow-y: auto;
    }

    .image-item {
        position: relative;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 4px 15px rgba(0,0,0,0.1);
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        cursor: pointer;
    }

    .image-item:hover {
        transform: translateY(-5px);
        box-shadow: 0 8px 25px rgba(0,0,0,0.15);
    }

    .image-item img {
        width: 100%;
        height: 300px;
        object-fit: cover;
    }

    .image-overlay {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        background: linear-gradient(transparent, rgba(0,0,0,1));
        color: white;
        padding: 10px;
        font-size: 0.9rem;
    }

    .empty-state {
        text-align: center;
        color: #999;
        padding: 60px 20px;
    }

    .empty-state i {
        font-size: 4rem;
        margin-bottom: 20px;
        color: #ddd;
    }

    .form-section {
        margin-bottom: 25px;
    }

    .form-section label {
        font-weight: 600;
        color: #2c3e50;
        margin-bottom: 8px;
        display: block;
    }

    .resolution-options {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 10px;
        margin-top: 10px;
    }

    .resolution-card {
        padding: 12px;
        border: 2px solid #e0e6ed;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.3s ease;
        text-align: center;
    }

    .resolution-card.active {
        border-color: #667eea;
        background: #f0f4ff;
        color: #667eea;
        font-weight: bold;
    }

    .resolution-card:hover {
        border-color: #667eea;
        background: #f8f9ff;
    }

    .examples-section {
        background: #f8f9fa;
        border-radius: 8px;
        margin-top: 20px;
    }

    .example-item {
        margin-bottom: 15px;
        padding: 12px;
        background: white;
        border-radius: 6px;
        transition: background 0.2s ease;
        border-left: 4px solid #667eea;
        display: flex;
        gap: 12px;
        align-items: flex-start;
    }

    .example-item:hover {
        background: #f0f4ff;
    }

    .example-thumbnail {
        width: 60px;
        height: 60px;
        border-radius: 8px;
        object-fit: cover;
        flex-shrink: 0;
        cursor: pointer;
        transition: transform 0.2s ease;
        border: 2px solid #e0e6ed;
    }

    .example-thumbnail:hover {
        transform: scale(1.05);
        border-color: #667eea;
    }

    .example-content {
        flex: 1;
        cursor: pointer;
    }

    .example-title {
        font-weight: bold;
        color: #2c3e50;
        margin-bottom: 5px;
    }

    .example-prompt {
        color: #666;
        font-size: 0.9rem;
        line-height: 1.4;
    }

    .generate-btn {
        width: 100%;
        height: 50px;
        font-size: 1.1rem;
        font-weight: bold;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border: none;
        border-radius: 25px;
        color: white;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
    }

    .generate-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
    }

    .generate-btn:disabled {
        opacity: 0.6;
        transform: none;
        cursor: not-allowed;
    }

    .el-textarea__inner {
        border-radius: 8px !important;
    }

    .el-input__inner {
        border-radius: 8px !important;
    }

    .loading-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0,0,0,0.7);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999;
    }

    .loading-content {
        background: white;
        padding: 40px;
        border-radius: 16px;
        text-align: center;
        max-width: 300px;
    }

    .stats-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        padding: 10px;
        background: #f0f4ff;
        border-radius: 8px;
    }

    @media (max-width: 768px) {
        .main-content {
            flex-direction: column;
        }
        
        .resolution-options {
            grid-template-columns: 1fr;
        }
    }
    p{
        margin-bottom: 5px;
    }
</style>